import { URL_QUERY_PARAM_INTERACTION_SESSION } from '@atlassiansox/analytics-cross-product-interaction-client/generate-url-with-params';

/**
 * The decoded contents of the `xpis` cross-product interaction param.
 *
 * Mirrors the shape written by the analytics client's `generateUrlWithParams`:
 * - `bridge` — the navigation component that produced the link, e.g. `searchPage`.
 * - `source` — the host experience, e.g. `jira-software` (product, or product-subProduct).
 * - `id` — the interaction session id.
 */
export type InteractionSession = {
	bridge: string;
	id: string;
	source: string;
};

/**
 * Decode the `xpis` param from a URL, or `null` if it is absent or unusable.
 *
 * This is the read side of cross-product URL wrapping. Reading `bridge` and
 * `source` back off the URL (rather than re-deriving them from props) means the
 * click analytic reports exactly what the link carries, so the two signals cannot
 * disagree. It also makes "was this link wrapped?" answerable from the URL alone:
 * no param means there is nothing to report.
 *
 * All three fields are required, matching the destination-side validation in
 * Analytics Web Client's `CrossProductInteractionTracking` — a partial payload is
 * treated as corrupt there, so it is not trusted here either.
 */
export const parseInteractionSession = (url: string | undefined): InteractionSession | null => {
	if (!url) {
		return null;
	}

	try {
		// A base is supplied so relative URLs parse; `generateUrlWithParams` accepts
		// them too, and it is stripped again because only the query is read.
		const { searchParams } = new URL(url, 'http://localhost/');
		const encoded = searchParams.get(URL_QUERY_PARAM_INTERACTION_SESSION);

		if (!encoded) {
			return null;
		}

		// `searchParams.get` has already percent-decoded the value.
		const decoded = JSON.parse(atob(encoded));

		if (!decoded?.bridge || !decoded?.id || !decoded?.source) {
			return null;
		}

		return { bridge: decoded.bridge, id: decoded.id, source: decoded.source };
	} catch {
		// Malformed base64, malformed JSON, or an unparseable URL. A corrupt param
		// must not break navigation, so it is treated as absent.
		return null;
	}
};
